import { FunctionComponent } from 'react';
import { isToday, parseISO, format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import AttachmentCard from './AttachmentCard';
import Avatar from '../../../components/common/Avatar';
import Divider from '../../../components/common/Divider';
import Label from '../../../components/Label';
import { COMPANY_NAME } from '../../../config';

const MessageThreadItem: FunctionComponent<{
  subject?: string;
  content: string;
  sender: string;
  senderType: 'Provider' | 'Patient';
  timeSent: string;
  avatarSrc?: string;
  showAvatar?: boolean;
  showTime?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  attachments?: Array<any>;
  downloadAttachment: any;
}> = ({
  subject,
  content,
  sender,
  senderType,
  timeSent,
  showAvatar = true,
  showTime = true,
  avatarSrc,
  containerRef,
  attachments,
  downloadAttachment,
}) => {
  const { t } = useTranslation();

  const displayDate = (timestamp: string) =>
    isToday(parseISO(timestamp)) ? t('Today') : format(parseISO(timestamp), 'MMM dd, yyyy');

  return (
    <div className='my-4 mb-1 ml-0 mr-0' ref={containerRef}>
      {showAvatar ? (
        <div className='message__thread__item-container'>
          <Avatar
            src={avatarSrc}
            fallbackString={sender}
            height={32}
            width={32}
            styles={{ placeholder: { fontSize: '16px' } }}
          />
          <Label className='mb-0'>
            <span>{sender}</span>
            {showTime && timeSent && (
              <>
                <br />
                <small className='fw-light'>
                  {displayDate(timeSent)} - {format(parseISO(timeSent), 'hh:mm a')}
                </small>
              </>
            )}
            <br />
          </Label>
        </div>
      ) : null}
      {subject ? (
        <h6 className='message__thread__item-subject text-truncate'>{t(subject)}</h6>
      ) : null}
      <div className='message__thread__item-content mb-4'>{t(content)}</div>
      <div className='message__thread__item-content text-gray-600 mb-1'>{t(sender)}</div>
      {senderType === 'Provider' && (
        <div className='message__thread__item-content text-gray-500 mb-1'>{t(COMPANY_NAME)}</div>
      )}
      {attachments?.length && (
        <div>
          <div className='mt-2 mb-2'>
            <Divider />
          </div>
          <div className='text-gray-500' style={{ fontSize: '14px', lineHeight: '20px' }}>
            {t('Attachments')} ({attachments.length})
          </div>
          <div className='row row-cols-2 row-cols-lg-2 g-2 g-lg-3 custom__upload-attachments'>
            {attachments.map((attachment: any, index: number) => (
              <div className='col p-2' key={index}>
                <AttachmentCard
                  url={attachment.url}
                  size={attachment.contentLength}
                  downloadAttachment={downloadAttachment}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageThreadItem;
